/**
 * Liens de distribution de l'app, en un seul endroit.
 *
 * Une URL de store vide veut dire "pas encore publie" : l'UI affiche alors
 * "Coming soon" au lieu d'un bouton mort. C'est le seul endroit a modifier le
 * jour de la mise en ligne.
 */

/**
 * Sans code pays dans le chemin : Apple renvoie chaque visiteur vers SON
 * storefront (verifie : /us/, /be/ et /fr/ repondent tous 200, l'app est
 * distribuee partout). Une URL `/be/` collee dans un partage enverrait un
 * Americain sur une page qu'il ne peut pas acheter. Meme logique que le lien
 * Play, qui n'a jamais eu de pays.
 */
export const APP_STORE_URL = 'https://apps.apple.com/app/upscale-guitar/id6758942686';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.christophenovalet.upscale';

/**
 * L'app complete dans un navigateur d'ordinateur. C'etait la sortie de secours
 * des plateformes pas encore publiees ; depuis que les deux stores sont en
 * ligne, c'est simplement la troisieme facon de jouer, celle qui ne demande
 * rien a installer.
 *
 * Slash final volontaire, meme raison que le QR : une redirection `/app` vers
 * `/app/` n'est pas garantie de conserver ce qui suit.
 */
export const DESKTOP_APP_URL = 'https://upscale.li/app/';
export const DESKTOP_APP_LABEL = 'upscale.li/app';

export const PLATFORMS = [
  { id: 'ios', label: 'iPhone & iPad', store: 'App Store', url: APP_STORE_URL },
  { id: 'android', label: 'Android', store: 'Google Play', url: PLAY_STORE_URL },
];

/**
 * Cle du jeton dans la chaine `referrer` de Google Play.
 *
 * Play rend cette chaine telle quelle a l'app au premier lancement (Install
 * Referrer API), en une seule fois et pour toujours. Une cle maison plutot
 * qu'un `utm_` : ce n'est pas une campagne, et les `utm_` sont lus par d'autres
 * outils qui n'ont rien a faire d'un jeton de session.
 *
 * Le nom ne bougera plus, meme si ce qu'il transporte a change (S5) : la page
 * web et l'app installee ne se deploient pas le meme jour, et c'est le binaire
 * deja pose sur le telephone qui relit cette cle.
 */
export const INVITE_REFERRER_KEY = 'upscale_invite';

/**
 * Le lien Play qui porte le jeton de PASSAGE du compte de la session : le
 * telephone qui installe depuis cette page arrive connecte sur le compte
 * choisi, sans rien saisir ni retourner dans l'email.
 *
 * Le jeton de passage et pas le jeton d'invitation (spec v5 F7, S5) : sur un
 * lien recu a une adresse et un compte d'app a une autre, l'invitation aurait
 * ouvert la premiere alors que la personne venait de choisir la seconde.
 * L'appelant le frappe au clic sur le bouton du store (cf. GetTheApp) : ce
 * jeton vit une demi-heure, le temps d'une installation Play, pas le temps
 * d'une page qui traine ouverte.
 *
 * Le JETON seul, jamais le code ni l'adresse : la chaine referrer traverse le
 * Play Store et ses journaux, comme une query string (garde-fou du plan v4). Le
 * jeton, lui, est opaque, s'echange une fois et ne vaut rien sans le serveur.
 *
 * Sans jeton vif, le lien Play ordinaire : rien a ajouter, rien a fuiter.
 */
export function buildPlayStoreUrl(referrerToken = '') {
  const token = (referrerToken || '').trim();
  if (!PLAY_STORE_URL || !token) return PLAY_STORE_URL;
  return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(`${INVITE_REFERRER_KEY}=${token}`)}`;
}

export const getPlatform = (id) => PLATFORMS.find((p) => p.id === id);

/**
 * Cible du QR code : la racine de l'app, pas l'URL d'un store.
 *
 * Pas le store, meme maintenant qu'ils existent tous les deux : un QR n'a qu'une
 * destination alors qu'il est scanne par les deux plateformes, et le code
 * imprime ou capture ne changera plus alors que l'URL d'un store, si. La racine
 * fait le tri elle-meme, cf. getDeviceStore.
 *
 * Pas /get non plus, malgre les apparences : l'app est deployee en statique sur
 * OVH, sans reecriture d'URL, donc une route qui ne correspond a aucun fichier
 * repond 404. La racine, elle, existe toujours, et un telephone qui l'ouvre est
 * de toute facon renvoye vers la page d'installation par isBrowserGated. Le
 * chemin le plus court passe par la seule URL dont on est sur.
 *
 * REACT_APP_INSTALL_URL la remplace le jour ou une URL courte lui est dediee.
 */
export function getInstallEntryUrl() {
  const configured = (process.env.REACT_APP_INSTALL_URL || '').trim();
  if (configured) return configured;
  const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
  return `${base.replace(/\/+$/, '')}/`;
}

/**
 * Lien d'invitation : la page qui rattache un code d'acces a un compte.
 *
 * Parametre de requete sur la RACINE, jamais une route. L'app est servie en
 * statique sur OVH sans reecriture d'URL : `/app/invite` repondrait 404 en
 * production alors que la racine existe toujours (meme raison que le QR
 * ci-dessus). Le slash avant le `?` est volontaire, une redirection `/app` vers
 * `/app/` ne garantit pas de conserver la requete.
 *
 * C'est la seule surface de rachat qui marche depuis un telephone : le
 * navigateur mobile est renvoye vers la page d'installation par isBrowserGated,
 * et les apps natives n'ont pas de champ de code (App Store 3.1.1). La page
 * s'affiche donc avant la gate, cf. index.js.
 */
export const INVITE_PARAM = 'invite';

/**
 * Token du lien personnel, quand le mail en porte un : `?invite=CODE&t=TOKEN`.
 *
 * Opaque et a usage unique. L'adresse du destinataire n'est jamais dans l'URL,
 * c'est le token qui la porte cote serveur : une URL traverse trop d'endroits
 * qui la gardent (logs, referrers, historique) pour y mettre une adresse.
 *
 * Le code reste a cote du token : c'est lui qui porte le repli le jour ou le
 * token a expire ou a deja servi.
 */
export const INVITE_TOKEN_PARAM = 't';

/**
 * Jeton de passage a usage unique dans le deep link "Open the app" (spec v5,
 * S2/S4) : upscale://invite?h=TOKEN. Un parametre distinct de `t` : le jeton
 * d'invitation et le jeton de passage n'ouvrent pas le meme compte, l'app ne
 * doit jamais confondre les deux.
 */
export const HANDOFF_TOKEN_PARAM = 'h';

/**
 * Le deep link que tape le bouton "Open the app" : le jeton de passage du
 * compte choisi, et RIEN d'autre. Ni code de rachat ni jeton d'invitation dans
 * le chemin natif (App Store 3.1.1 et spec v5 F10) : l'acces est deja pose
 * cote compte, ce lien ne sait qu'ouvrir une session.
 *
 * Un scheme custom, pas l'universal link https : depuis S4 le chemin
 * d'invitation est sorti des universal links (le lien https ouvre TOUJOURS le
 * navigateur), le scheme est le seul canal vers l'app.
 *
 * Sans jeton (emission en echec), le scheme nu ouvre simplement l'app : la
 * personne se connecte par le paywall ou Settings, l'acces est dormant.
 */
export function buildNativeHandoffUrl(handoffToken) {
  const token = (handoffToken || '').trim();
  if (!token) return 'upscale://invite';
  const params = new URLSearchParams();
  params.set(HANDOFF_TOKEN_PARAM, token);
  return `upscale://invite?${params.toString()}`;
}

/**
 * Le meme lien en https, pour le repli quand le scheme ne trouve pas d'app :
 * sur Android, l'app link verifie peut encore ouvrir l'app ; sinon la page
 * d'invitation se raffiche, et le token deja echange de la session n'y est pas
 * rejoue (garde dans InvitePage).
 */
export function buildWebInviteUrl(code, token) {
  const params = new URLSearchParams();
  if (code) params.set(INVITE_PARAM, code);
  if (token) params.set(INVITE_TOKEN_PARAM, token);
  return `${getInstallEntryUrl()}?${params.toString()}`;
}

export function getInviteToken() {
  if (typeof window === 'undefined') return '';
  try {
    return (new URLSearchParams(window.location.search).get(INVITE_TOKEN_PARAM) || '').trim();
  } catch {
    return '';
  }
}

export function getInviteCode() {
  if (typeof window === 'undefined') return '';
  try {
    const raw = new URLSearchParams(window.location.search).get(INVITE_PARAM) || '';
    return raw.trim().toUpperCase();
  } catch {
    return '';
  }
}

/**
 * Le store qui correspond a l'appareil qui regarde la page, ou null sur desktop.
 * Sert a n'afficher un QR que la ou il a du sens : personne ne scanne l'ecran
 * qu'il tient deja dans la main.
 *
 * L'iPad se declare comme un Mac depuis iPadOS 13, d'ou le test sur les points
 * de contact (meme heuristique que getBrowserDeviceType).
 */
export function getDeviceStore() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  const isIPadAsMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPad|iPhone|iPod/.test(ua) || isIPadAsMac) return getPlatform('ios');
  if (/Android/.test(ua)) return getPlatform('android');
  return null;
}

/**
 * Les deux endroits ou on parle entre joueurs. Meme regle qu'une URL de store
 * vide : tant que le groupe n'existe pas, la rangee ne s'affiche pas plutot que
 * de mener nulle part.
 */
export const MESSENGER_GROUP_URL = '';
export const DISCORD_URL = 'https://discord.gg/9ZzXYaJpMs';
